import * as Sentry from '@sentry/react';
import { classifyErrorOrigin, foreignErrorFingerprint, isForeignNetworkNoise } from './errorSource.js';
import { errorFingerprint } from './fingerprint.js';
import { runPostCaptureHooks } from './hooks.js';
import { isInjectedScriptNoise } from './injectedScript.js';
import { isUnreportedError } from './unreported.js';

export interface SentryConfig {
  dsn?: string;
  environment: string;
  release?: string;
  debug?: boolean;
  /** Whether Sentry is enabled. When false, events are not sent. */
  enabled: boolean;
  app: string;
  team: string;
  /** TanStack Router instance for route-aware tracing and navigation breadcrumbs */
  router: Parameters<typeof Sentry.tanstackRouterBrowserTracingIntegration>[0];
  /** Tunnel URL to proxy Sentry envelopes through (bypasses ad blockers) */
  tunnel?: string;
  /**
   * Build-time `applicationKey` marker injected by the Sentry Vite plugin.
   * Must match the value passed to `sentryVitePlugin({ applicationKey })`.
   * When set, the `thirdPartyErrorFilterIntegration` is registered with this
   * key so vendor stack frames (Sourcepoint, GTM tags, extensions, …) get
   * tagged `third_party_code: true`.
   */
  applicationKey?: string;
  /** Hosts that keep Sentry's default grouping instead of the coarse per-vendor fingerprint, e.g. the payment SDKs. */
  criticalVendors?: readonly string[];
  /** Surface-specific noise on top of the built-in list. Bundler wording belongs to whoever builds. */
  ignoreErrors?: readonly (string | RegExp)[];
}

export interface SentrySamplingRates {
  errors?: number;
  traces?: number;
  replaysSession?: number;
  replaysOnError?: number;
}

const samplingRates = {
  errors: 1,
  traces: 0.1,
  replaysSession: 0,
  replaysOnError: 0,
};

let replayLoaded = false;

export function updateSentrySamplingRates(rates: SentrySamplingRates) {
  if (rates.errors != null) {
    samplingRates.errors = rates.errors;
  }
  if (rates.traces != null) {
    samplingRates.traces = rates.traces;
  }
  if (rates.replaysSession != null) {
    samplingRates.replaysSession = rates.replaysSession;
  }
  if (rates.replaysOnError != null) {
    samplingRates.replaysOnError = rates.replaysOnError;
  }

  const wantsReplay = samplingRates.replaysSession > 0 || samplingRates.replaysOnError > 0;
  if (wantsReplay && !replayLoaded) {
    replayLoaded = true;
    void import('./replay.js')
      .then(({ loadReplay }) => loadReplay())
      .catch(() => {
        /* chunk-load failure — safe to ignore */
      });
  }
}

export function getSamplingRates() {
  return { ...samplingRates };
}

let initialized = false;

export function initSentry(config: SentryConfig) {
  if (initialized) {
    return;
  }
  initialized = true;

  // Not read in `beforeSend`: Sentry keeps the options for the client's lifetime, config included.
  const { criticalVendors } = config;

  Sentry.init({
    dsn: config.dsn,
    tunnel: config.tunnel,
    enabled: config.enabled,
    environment: config.environment,
    release: config.release,
    debug: config.debug,
    initialScope: {
      tags: {
        app: config.app,
        team: config.team,
      },
    },

    integrations: (defaults) => [
      // Remove auto-session tracking — session pings add noise without auth context
      ...defaults.filter((i) => i.name !== 'BrowserSession'),
      Sentry.tanstackRouterBrowserTracingIntegration(config.router),
      // `exclusively`: native and anonymous frames never carry the marker, so `if-contains` tagged our
      // own errors third-party. `error_source` from beforeSend is the signal to alert on.
      ...(config.applicationKey
        ? [
            Sentry.thirdPartyErrorFilterIntegration({
              filterKeys: [config.applicationKey],
              behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
            }),
          ]
        : []),
    ],

    tracesSampler: () => samplingRates.traces,
    // Replay rates are read at lazy-load time in replay.ts via getSamplingRates()
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    beforeSend(event, hint) {
      const statusCode = event.contexts?.response?.status_code;
      if (statusCode === 401 || statusCode === 403) {
        return null;
      }

      if (samplingRates.errors === 0) {
        return null;
      }

      if (isInjectedScriptNoise(event)) {
        return null;
      }

      if (isUnreportedError(hint.originalException)) {
        return null;
      }

      const origin = classifyErrorOrigin(event);
      if (isForeignNetworkNoise(event, origin, criticalVendors)) {
        return null;
      }

      event.tags = { ...event.tags, error_source: origin.source };
      if (origin.vendor) {
        event.tags.error_vendor = origin.vendor;
      }

      // ??=: a capture-site fingerprint (scope.setFingerprint, e.g. enrichSentryScope) wins;
      // the error's own fingerprint only fills the default.
      event.fingerprint ??= errorFingerprint(hint.originalException) ?? foreignErrorFingerprint(event, origin, criticalVendors);

      if (event.type === undefined) {
        runPostCaptureHooks(event);
      }

      return event;
    },

    ignoreErrors: [
      // Stack-less / anonymous-frame noise the thirdPartyErrorFilterIntegration
      // can't classify. Each pattern is backed by recent observed events.
      /runtime\.sendMessage/, // DuckDuckGo extension
      /xbrowser is not defined/, // Chinese OEM browser auto-fill
      /swbrowser is not defined/, // same
      /WKWebView API client/, // DuckDuckGo iOS WKWebView
      /__firefox__/, // Firefox iOS reader / browser bridge
      /Object Not Found Matching Id:/, // Outlook SafeLinks / Office link scanner bridge
      // User cancellations
      'AbortError',
      'The operation was aborted',
      ...(config.ignoreErrors ?? []),
    ],

    denyUrls: [
      // Facebook In-App Browser autofill
      /iabjs:\/\//,
    ],
  });
}
